/**
 * API Utilities
 * Safe JSON parsing and error handling for API responses
 */

import { AUTH_FRIENDLY_ERRORS } from '@parlay-picker/shared/authMessages';

export interface ApiErrorResponse {
  error: string;
  errorType: string;
}

/**
 * Safely parse JSON from a fetch response
 * Returns user-friendly error messages instead of technical errors
 */
export async function safeParseJson<T = unknown>(res: Response): Promise<T | ApiErrorResponse> {
  try {
    const text = await res.text();
    if (!text || text.trim() === '') {
      return {
        error: 'Server returned an empty response. Please try again.',
        errorType: 'api_error',
      };
    }
    return JSON.parse(text) as T;
  } catch (_err) {
    return {
      error: 'Failed to process server response. Please try again.',
      errorType: 'api_error',
    };
  }
}

/**
 * Get user-friendly error message from error object
 */
export function getUserFriendlyError(err: Error | string | null | undefined): string {
  const message = (err as Error)?.message || err || 'Unknown error';

  // Map technical errors to user-friendly messages
  const technicalErrors: Record<string, string> = {
    'Unexpected end of JSON input': 'Failed to process server response. Please try again.',
    'Unexpected token': 'Failed to process server response. Please try again.',
    'Failed to fetch': 'Network error. Please check your connection and try again.',
    NetworkError: 'Network error. Please check your connection and try again.',
    'Load failed': 'Network error. Please check your connection and try again.',
    ECONNREFUSED: 'Server is unavailable. Please try again later.',
    ETIMEDOUT: 'Request timed out. Please try again.',
    'Request failed': 'Something went wrong. Please try again.',
    ...AUTH_FRIENDLY_ERRORS,
  };

  // Check if error matches any technical pattern
  for (const [pattern, friendlyMessage] of Object.entries(technicalErrors)) {
    if (String(message).includes(pattern)) {
      return friendlyMessage;
    }
  }

  // Return original message if no pattern matched (it's already user-friendly)
  return String(message);
}
